import React from "react";

const S = {
  wrapper:      { marginTop:'10px', marginLeft:'0px', border:'3px', padding:'40px', borderStyle:'solid', borderColor:'grey', fontFamily:"'Georgia', serif", color:'#1a1a2e', lineHeight:'1.7' },
  h1:           { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #2E75B6', paddingBottom:'8px', marginBottom:'24px', color:'#1F3864' },
  h2:           { fontSize:'1.3rem', fontWeight:'700', color:'#2E75B6', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #2E75B6', paddingLeft:'10px' },
  p:            { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox:      { background:'#EBF3FB', border:'2px solid #2E75B6', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#1F3864', marginBottom:'8px' },
  infoBoxItem:  { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #2E75B6' },
  table:        { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th:           { background:'#2E75B6', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE:          { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#f5f9ff' },
  tdO:          { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#fff' },
  tdL:          { fontWeight:'600', color:'#1F3864', whiteSpace:'nowrap', width:'220px' },
  code:         { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap:        { margin:'24px 0', background:'#f8fbff', border:'1px solid #ccd9ea', borderRadius:'8px', padding:'20px' },
  dTitle:       { fontWeight:'700', fontSize:'0.95rem', color:'#1F3864', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption:     { fontSize:'0.84rem', color:'#555', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Formula / Notes</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 1 — Slope Field for dy/dx = y − x
// Left: direction field with solution curves
// Right: concept summary cards
// ─────────────────────────────────────────────────────────────────
function DiagramSlopeField() {
  const W = 900, H = 345;
  const aX = 56, aY = 36, aW = 456, aH = 256;
  const xMin = -2, xMax = 4, yMin = -2, yMax = 4;
  const toX = v => aX + (v - xMin) / (xMax - xMin) * aW;
  const toY = v => aY + aH - (v - yMin) / (yMax - yMin) * aH;
  const f = (x, y) => y - x;

  // 13×13 grid of slope ticks
  const marks = [];
  for (let ci = 0; ci < 13; ci++) {
    for (let ri = 0; ri < 13; ri++) {
      const x = xMin + (xMax - xMin) * ci / 12;
      const y = yMin + (yMax - yMin) * ri / 12;
      const slope = Math.max(-5, Math.min(5, f(x, y)));
      const ang = Math.atan(slope);
      const L = 13;
      const dx = Math.cos(ang) * L / 2;
      const dy = Math.sin(ang) * L / 2;
      const t = (slope + 5) / 10;
      const r = Math.round(46  + t * (192 - 46));
      const g = Math.round(117 + t * (57  - 117));
      const b = Math.round(182 + t * (0   - 182));
      marks.push({ cx: toX(x), cy: toY(y), dx, dy, col: `rgb(${r},${g},${b})` });
    }
  }

  // Solution curves y = C·e^x + x + 1
  const Cvals = [-2, -1, -0.4, 0, 0.7, 1.6];
  const cCols = ['#9b59b6','#c0392b','#e07b00','#27ae60','#2E75B6','#1F3864'];

  const panX = 550, panW = W - panX - 12;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Slope Field (Direction Field) — dy/dx = y − x with Solution Curves y = Ce^x + x + 1</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* LEFT PANEL */}
        <rect x={10} y={8} width={530} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <rect x={14} y={12} width={522} height={22} rx={3} fill="#1F3864"/>
        <text x={275} y={27} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">
          dy/dx = y − x  ·  tick angle = slope at (x,y)  ·  colour = sign of slope
        </text>

        <clipPath id="sf1">
          <rect x={aX} y={aY} width={aW} height={aH}/>
        </clipPath>

        {/* Grid */}
        {[-2,-1,0,1,2,3,4].map(v => (
          <g key={v}>
            <line x1={toX(v)} y1={aY} x2={toX(v)} y2={aY+aH} stroke="#f0f0f0" strokeWidth="0.7"/>
            <line x1={aX} y1={toY(v)} x2={aX+aW} y2={toY(v)} stroke="#f0f0f0" strokeWidth="0.7"/>
          </g>
        ))}
        {/* Axes */}
        <line x1={aX} y1={toY(0)} x2={aX+aW} y2={toY(0)} stroke="#bbb" strokeWidth="1.4"/>
        <line x1={toX(0)} y1={aY} x2={toX(0)} y2={aY+aH} stroke="#bbb" strokeWidth="1.4"/>

        {/* Zero isocline y = x */}
        <line x1={toX(-2)} y1={toY(-2)} x2={toX(4)} y2={toY(4)}
          stroke="#27ae60" strokeWidth="2" strokeDasharray="7,4" clipPath="url(#sf1)"/>
        <text x={toX(3.4)+3} y={toY(3.4)-6} fontSize="9.5" fill="#27ae60" fontWeight="700">y=x (slope=0)</text>

        {/* Slope marks */}
        <g clipPath="url(#sf1)">
          {marks.map(({cx,cy,dx,dy,col},i) => (
            <line key={i} x1={cx-dx} y1={cy+dy} x2={cx+dx} y2={cy-dy}
              stroke={col} strokeWidth="1.4" opacity="0.8"/>
          ))}
        </g>

        {/* Solution curves */}
        {Cvals.map((C, ci) => {
          const pts = [];
          for (let i = 0; i <= 120; i++) {
            const x = xMin + (xMax - xMin) * i / 120;
            const y = C * Math.exp(x) + x + 1;
            if (y >= yMin - 0.1 && y <= yMax + 0.1)
              pts.push(`${toX(x).toFixed(1)},${toY(y).toFixed(1)}`);
          }
          if (pts.length < 2) return null;
          return <polyline key={C} points={pts.join(' ')} fill="none"
            stroke={cCols[ci]} strokeWidth="2.2" clipPath="url(#sf1)"/>;
        })}

        {/* Axis tick labels */}
        {[-2,-1,1,2,3,4].map(v => (
          <g key={v}>
            <text x={toX(v)} y={toY(0)+14} textAnchor="middle" fontSize="9" fill="#888">{v}</text>
            <text x={toX(0)-9} y={toY(v)+4} textAnchor="end" fontSize="9" fill="#888">{v}</text>
          </g>
        ))}
        <text x={aX+aW+7} y={toY(0)+5} fontSize="10" fill="#555">x</text>
        <text x={toX(0)+5} y={aY-7} fontSize="10" fill="#555">y</text>

        {/* C labels at right edge */}
        {Cvals.map((C, ci) => {
          const y = C * Math.exp(xMax) + xMax + 1;
          if (y < aY || y > aY+aH) return null;
          return <text key={C} x={toX(xMax)+3} y={toY(C*Math.exp(xMax)+xMax+1)+4}
            fontSize="9.5" fill={cCols[ci]} fontWeight="700">C={C}</text>;
        })}

        {/* Legend */}
        <line x1={aX+6} y1={aY+aH+20} x2={aX+24} y2={aY+aH+20} stroke="#c0392b" strokeWidth="2.2"/>
        <text x={aX+28} y={aY+aH+24} fontSize="9.5" fill="#444">solution y=Ce^x+x+1</text>
        <line x1={aX+210} y1={aY+aH+20} x2={aX+228} y2={aY+aH+20}
          stroke="#27ae60" strokeWidth="2" strokeDasharray="6,3"/>
        <text x={aX+232} y={aY+aH+24} fontSize="9.5" fill="#444">zero isocline y=x</text>

        {/* RIGHT PANEL */}
        <rect x={panX} y={8} width={panW} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={panX+panW/2} y={26} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">Key Concepts</text>

        {[
          { y:32,  bg:'#EBF3FB', bc:'#2E75B6', title:'Slope field',
            lines:['Tick at (x,y): angle=f(x,y)','Tangent to solution curve','Drawn without solving ODE','Solutions never cross'] },
          { y:122, bg:'#f0fff4',  bc:'#27ae60',  title:'Isoclines',
            lines:['Curve where dy/dx = k','Zero isocline: y−x=0','i.e. y=x  (all slopes=0)','Guides sketch of solutions'] },
          { y:212, bg:'#fff0f0',  bc:'#c0392b',  title:'Family of solutions',
            lines:['y = Ce^x + x + 1','One curve per value of C','IC y(x₀)=y₀ fixes C','→ unique particular soln.'] },
        ].map(({ y, bg, bc, title, lines }) => (
          <g key={y}>
            <rect x={panX+8} y={y} width={panW-16} height={84} rx={4} fill={bg} stroke={bc} strokeWidth="1.3"/>
            <text x={panX+18} y={y+16} fontSize="11" fill={bc} fontWeight="700">{title}</text>
            {lines.map((ln, j) => (
              <text key={j} x={panX+18} y={y+32+j*16} fontSize="10.5"
                fill={j<2?'#1F3864':'#555'}
                fontFamily={j<2?"'Courier New',monospace":"'Georgia',serif"}
                fontWeight={j<2?'700':'400'}>{ln}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        A slope field draws a short tick at every grid point (x, y) whose angle equals f(x, y) = y − x. Solution curves are everywhere tangent to the ticks and never intersect (Uniqueness Theorem). The green dashed line is the zero isocline y = x, where every tick is horizontal. Red-to-navy curves show y = Ce^x + x + 1 for six values of C, found by the integrating-factor method.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 2 — Separable ODE: dy/dx = x·y
// Left: solution family; Right: 4-step method cards
// ─────────────────────────────────────────────────────────────────
function DiagramSeparable() {
  const W = 900, H = 348;
  const aX = 56, aY = 36, aW = 374, aH = 258;
  const xMin = -2.8, xMax = 2.8, yMin = -4, yMax = 4;
  const toX = v => aX + (v - xMin) / (xMax - xMin) * aW;
  const toY = v => aY + aH - (v - yMin) / (yMax - yMin) * aH;

  const Cvals = [-1.5,-0.8,-0.3,0.3,0.8,1.5];
  const cCols = ['#9b59b6','#c0392b','#e07b00','#27ae60','#2E75B6','#1F3864'];

  const panX = 474, panW = W - panX - 12;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Separable ODE — dy/dx = x·y  ·  Solution Family y = Ce^(x²/2)</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        <rect x={10} y={8} width={454} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <rect x={14} y={12} width={446} height={22} rx={3} fill="#1F3864"/>
        <text x={241} y={27} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">
          Solution family y = Ce^(x²/2)  ·  equilibrium y = 0 (orange dashed)
        </text>

        <clipPath id="sep1"><rect x={aX} y={aY} width={aW} height={aH}/></clipPath>

        {[-2,-1,0,1,2].map(v => (
          <g key={v}>
            <line x1={toX(v)} y1={aY} x2={toX(v)} y2={aY+aH} stroke="#f0f0f0" strokeWidth="0.7"/>
            <line x1={aX} y1={toY(v)} x2={aX+aW} y2={toY(v)} stroke="#f0f0f0" strokeWidth="0.7"/>
          </g>
        ))}
        <line x1={aX} y1={toY(0)} x2={aX+aW} y2={toY(0)} stroke="#bbb" strokeWidth="1.4"/>
        <line x1={toX(0)} y1={aY} x2={toX(0)} y2={aY+aH} stroke="#bbb" strokeWidth="1.4"/>

        {/* Equilibrium y=0 */}
        <line x1={aX} y1={toY(0)} x2={aX+aW} y2={toY(0)}
          stroke="#e07b00" strokeWidth="2.2" strokeDasharray="7,4" clipPath="url(#sep1)"/>
        <text x={aX+aW-4} y={toY(0)+14} textAnchor="end" fontSize="9.5" fill="#e07b00" fontWeight="700">y=0  (equilibrium)</text>

        {/* Curves */}
        {Cvals.map((C, ci) => {
          const pts = [];
          for (let i = 0; i <= 120; i++) {
            const x = xMin + (xMax-xMin)*i/120;
            const y = C*Math.exp(x*x/2);
            if (y>yMin-0.1&&y<yMax+0.1)
              pts.push(`${toX(x).toFixed(1)},${toY(y).toFixed(1)}`);
          }
          if (pts.length<2) return null;
          return <polyline key={C} points={pts.join(' ')} fill="none"
            stroke={cCols[ci]} strokeWidth="2.2" clipPath="url(#sep1)"/>;
        })}

        {/* C labels at x=0 */}
        {Cvals.map((C, ci) => {
          if (C<yMin||C>yMax) return null;
          return <text key={C} x={toX(0)+5} y={toY(C)-4}
            fontSize="9.5" fill={cCols[ci]} fontWeight="700">C={C}</text>;
        })}

        {[-2,-1,1,2].map(v => (
          <g key={v}>
            <text x={toX(v)} y={toY(0)+14} textAnchor="middle" fontSize="9" fill="#888">{v}</text>
            <text x={toX(0)-8} y={toY(v)+4} textAnchor="end" fontSize="9" fill="#888">{v}</text>
          </g>
        ))}
        <text x={aX+aW+7} y={toY(0)+5} fontSize="10" fill="#555">x</text>
        <text x={toX(0)+5} y={aY-7} fontSize="10" fill="#555">y</text>

        {/* RIGHT: numbered steps */}
        <rect x={panX} y={8} width={panW} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={panX+panW/2} y={26} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">
          Separation of Variables
        </text>

        {[
          { y:32,  bc:'#2E75B6', bg:'#EBF3FB', n:'1', step:'Identify',
            lines:['dy/dx = h(x)·g(y)','h(x)=x,  g(y)=y','Check g(y)≠0 first'] },
          { y:108, bc:'#27ae60', bg:'#f0fff4',  n:'2', step:'Separate',
            lines:['dy/y = x dx','y-terms → left','x-terms → right'] },
          { y:184, bc:'#e07b00', bg:'#fff7ee',  n:'3', step:'Integrate',
            lines:['∫dy/y = ∫x dx','ln|y| = x²/2 + C₁','One constant suffices'] },
          { y:260, bc:'#c0392b', bg:'#fff0f0',  n:'4', step:'Solve for y',
            lines:['|y|=e^(x²/2+C₁)','y = Ce^(x²/2)','C = ±e^C₁'] },
        ].map(({ y, bc, bg, n, step, lines }) => (
          <g key={y}>
            <rect x={panX+8} y={y} width={panW-16} height={70} rx={4} fill={bg} stroke={bc} strokeWidth="1.3"/>
            <circle cx={panX+22} cy={y+16} r={12} fill={bc}/>
            <text x={panX+22} y={y+21} textAnchor="middle" fontSize="11" fill="#fff" fontWeight="700">{n}</text>
            <text x={panX+40} y={y+16} fontSize="11" fill={bc} fontWeight="700">{step}</text>
            {lines.map((ln,j) => (
              <text key={j} x={panX+40} y={y+32+j*15} fontSize="10.5"
                fill={j===0?'#1F3864':'#555'}
                fontFamily={j===0?"'Courier New',monospace":"'Georgia',serif"}
                fontWeight={j===0?'700':'400'}>{ln}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        A separable ODE dy/dx = h(x)·g(y) allows all y-terms to move to the left and x-terms to the right before integrating. The constant C absorbs both integration constants and is fixed by an initial condition. The equilibrium y = 0 (where g(0) = 0) must be checked separately — dividing by g(y) excludes it from the general formula.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 3 — Linear First-Order: Integrating Factor
// dy/dx + (2/x)y = x²  →  y = x³/5 + C/x²
// ─────────────────────────────────────────────────────────────────
function DiagramLinear() {
  const W = 900, H = 348;
  const aX = 56, aY = 36, aW = 414, aH = 258;
  const xMin = 0.25, xMax = 3.5, yMin = -1.5, yMax = 6;
  const toX = v => aX + (v-xMin)/(xMax-xMin)*aW;
  const toY = v => aY + aH - (v-yMin)/(yMax-yMin)*aH;

  const Cvals = [-0.8,-0.3,0,0.4,4/5,1.2];
  const cCols = ['#9b59b6','#e07b00','#27ae60','#888','#c0392b','#2E75B6'];

  const panX = 510, panW = W - panX - 12;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Linear First-Order ODE — dy/dx + (2/x)y = x²  ·  Integrating Factor μ(x) = x²</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        <rect x={10} y={8} width={490} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <rect x={14} y={12} width={482} height={22} rx={3} fill="#1F3864"/>
        <text x={259} y={27} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">
          General solution y = x³/5 + C/x²  ·  red bold curve: y(1)=1, C=4/5
        </text>

        <clipPath id="lin1"><rect x={aX} y={aY} width={aW} height={aH}/></clipPath>

        {[0.5,1,1.5,2,2.5,3,3.5].map(v => (
          <line key={`gx${v}`} x1={toX(v)} y1={aY} x2={toX(v)} y2={aY+aH} stroke="#f0f0f0" strokeWidth="0.7"/>
        ))}
        {[-1,0,1,2,3,4,5,6].filter(v=>v>=yMin).map(v => (
          <line key={`gy${v}`} x1={aX} y1={toY(v)} x2={aX+aW} y2={toY(v)}
            stroke={v===0?'#ddd':'#f0f0f0'} strokeWidth={v===0?1.2:0.7}/>
        ))}
        <line x1={aX} y1={toY(0)} x2={aX+aW} y2={toY(0)} stroke="#bbb" strokeWidth="1.4"/>
        <line x1={aX} y1={aY}    x2={aX}     y2={aY+aH}  stroke="#bbb" strokeWidth="1.2"/>

        {Cvals.map((C,ci) => {
          const pts=[];
          for(let i=0;i<=120;i++){
            const x=xMin+(xMax-xMin)*i/120;
            const y=x*x*x/5+C/(x*x);
            if(y>=yMin-0.1&&y<=yMax+0.1)
              pts.push(`${toX(x).toFixed(1)},${toY(y).toFixed(1)}`);
          }
          if(pts.length<2)return null;
          const isP=Math.abs(C-4/5)<0.001;
          return <polyline key={ci} points={pts.join(' ')} fill="none"
            stroke={cCols[ci]} strokeWidth={isP?3.2:1.8} opacity={isP?1:0.65}
            clipPath="url(#lin1)"/>;
        })}

        {/* IC dot */}
        <circle cx={toX(1)} cy={toY(1)} r={7} fill="#c0392b" stroke="#fff" strokeWidth="2"/>
        <text x={toX(1)+11} y={toY(1)-8} fontSize="10.5" fill="#c0392b" fontWeight="700">(1,1) IVP</text>

        {/* μ strip */}
        <rect x={14} y={aY+aH+10} width={482} height={30} rx={3} fill="#EBF3FB" stroke="#2E75B6"/>
        <text x={259} y={aY+aH+22} textAnchor="middle" fontSize="10"
          fill="#2E75B6" fontFamily="'Courier New',monospace" fontWeight="700">
          μ(x)=e^(∫2/x dx)=x²  →  d/dx[x²y]=x⁴  →  x²y=x⁵/5+C  →  y=x³/5+C/x²
        </text>
        <text x={259} y={aY+aH+34} textAnchor="middle" fontSize="9" fill="#555">
          Multiply ODE by μ; left side becomes exact derivative; integrate both sides directly
        </text>

        {[0.5,1,1.5,2,2.5,3,3.5].map(v=>(
          <text key={v} x={toX(v)} y={toY(0)+14} textAnchor="middle" fontSize="9" fill="#888">{v}</text>
        ))}
        {[-1,0,1,2,3,4,5].filter(v=>v>=yMin).map(v=>(
          <text key={v} x={aX-5} y={toY(v)+4} textAnchor="end" fontSize="9" fill="#888">{v}</text>
        ))}
        <text x={aX+aW+7} y={toY(0)+5} fontSize="10" fill="#555">x</text>
        <text x={aX+4} y={aY-7} fontSize="10" fill="#555">y</text>

        {/* RIGHT: four steps */}
        <rect x={panX} y={8} width={panW} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={panX+panW/2} y={26} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">
          Integrating Factor
        </text>

        {[
          { y:32,  bc:'#2E75B6', bg:'#EBF3FB', n:'1', step:'Standard form',
            lines:["dy/dx + P(x)y = Q(x)",'P=2/x,  Q=x²','Rearrange first'] },
          { y:112, bc:'#27ae60', bg:'#f0fff4',  n:'2', step:'Find μ(x)',
            lines:['μ = e^(∫P dx)','= e^(∫2/x dx) = x²','No +C needed here'] },
          { y:192, bc:'#e07b00', bg:'#fff7ee',  n:'3', step:'Multiply & integrate',
            lines:['x²y′+2xy = x⁴','d/dx[x²y] = x⁴','x²y = x⁵/5 + C'] },
          { y:272, bc:'#c0392b', bg:'#fff0f0',  n:'4', step:'Apply IC  y(1)=1',
            lines:['1 = 1/5 + C','C = 4/5','y = x³/5 + 4/(5x²)'] },
        ].map(({ y, bc, bg, n, step, lines }) => (
          <g key={y}>
            <rect x={panX+8} y={y} width={panW-16} height={74} rx={4} fill={bg} stroke={bc} strokeWidth="1.3"/>
            <circle cx={panX+22} cy={y+16} r={12} fill={bc}/>
            <text x={panX+22} y={y+21} textAnchor="middle" fontSize="11" fill="#fff" fontWeight="700">{n}</text>
            <text x={panX+40} y={y+16} fontSize="11" fill={bc} fontWeight="700">{step}</text>
            {lines.map((ln,j)=>(
              <text key={j} x={panX+40} y={y+32+j*15} fontSize="10.5"
                fill={j===0?'#1F3864':'#555'}
                fontFamily={j===0?"'Courier New',monospace":"'Georgia',serif"}
                fontWeight={j===0?'700':'400'}>{ln}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        Multiplying a linear ODE dy/dx + P(x)y = Q(x) by the integrating factor μ(x) = e^(∫P dx) converts the left side into the exact derivative d/dx[μy], so both sides can be integrated directly. The red bold curve passes through (1, 1), uniquely selecting C = 4/5. Lighter curves show the rest of the one-parameter family for other values of C.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 4 — Exact ODE: level curves of F(x,y) = x²y + xy²
// ─────────────────────────────────────────────────────────────────
function DiagramExact() {
  const W = 900, H = 340;
  const aX = 56, aY = 32, aW = 374, aH = 256;
  const xMin = -2.4, xMax = 2.4, yMin = -2.4, yMax = 2.4;
  const toX = v => aX + (v-xMin)/(xMax-xMin)*aW;
  const toY = v => aY + aH - (v-yMin)/(yMax-yMin)*aH;
  const F = (x,y) => x*x*y + x*y*y;

  const Cset  = [-4,-2,-1,0,1,2,4];
  const cCols = ['#9b59b6','#c0392b','#e07b00','#aaa','#27ae60','#2E75B6','#1F3864'];
  const NG = 64;

  const levelSegs = Cset.map((C,ci) => {
    const segs=[];
    for(let i=0;i<NG;i++){
      for(let j=0;j<NG;j++){
        const x0=xMin+(xMax-xMin)*i/NG, x1=xMin+(xMax-xMin)*(i+1)/NG;
        const y0=yMin+(yMax-yMin)*j/NG, y1=yMin+(yMax-yMin)*(j+1)/NG;
        const f00=F(x0,y0)-C, f10=F(x1,y0)-C, f01=F(x0,y1)-C, f11=F(x1,y1)-C;
        const pts=[];
        if(f00*f10<0){const t=f00/(f00-f10);pts.push([x0+t*(x1-x0),y0]);}
        if(f10*f11<0){const t=f10/(f10-f11);pts.push([x1,y0+t*(y1-y0)]);}
        if(f11*f01<0){const t=f11/(f11-f01);pts.push([x1-t*(x1-x0),y1]);}
        if(f01*f00<0){const t=f01/(f01-f00);pts.push([x0,y1-t*(y1-y0)]);}
        if(pts.length>=2)segs.push([pts[0],pts[1]]);
      }
    }
    return {C,segs,col:cCols[ci]};
  });

  const panX = 474, panW = W - panX - 12;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Exact ODE — (2xy+y²)dx + (x²+2xy)dy = 0  ·  Implicit Solutions F(x,y) = x²y+xy² = C</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        <rect x={10} y={8} width={454} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <rect x={14} y={12} width={446} height={22} rx={3} fill="#1F3864"/>
        <text x={241} y={27} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">
          Implicit solutions F(x,y)=C  ·  each colour = one level curve = one solution
        </text>

        <clipPath id="ex1"><rect x={aX} y={aY} width={aW} height={aH}/></clipPath>

        {[-2,-1,0,1,2].map(v=>(
          <g key={v}>
            <line x1={toX(v)} y1={aY} x2={toX(v)} y2={aY+aH} stroke="#f0f0f0" strokeWidth="0.7"/>
            <line x1={aX} y1={toY(v)} x2={aX+aW} y2={toY(v)} stroke="#f0f0f0" strokeWidth="0.7"/>
          </g>
        ))}
        <line x1={aX} y1={toY(0)} x2={aX+aW} y2={toY(0)} stroke="#bbb" strokeWidth="1.4"/>
        <line x1={toX(0)} y1={aY} x2={toX(0)} y2={aY+aH} stroke="#bbb" strokeWidth="1.4"/>

        <g clipPath="url(#ex1)">
          {levelSegs.map(({C,segs,col})=>
            segs.map(([p0,p1],si)=>(
              <line key={`${C}-${si}`}
                x1={toX(p0[0]).toFixed(1)} y1={toY(p0[1]).toFixed(1)}
                x2={toX(p1[0]).toFixed(1)} y2={toY(p1[1]).toFixed(1)}
                stroke={col} strokeWidth="1.8" opacity="0.85"/>
            ))
          )}
        </g>

        {/* C labels near right edge */}
        {Cset.map((C,ci)=>{
          const disc=16+8*C;
          if(disc<0)return null;
          const y0=(-4+Math.sqrt(disc))/4;
          if(y0<yMin||y0>yMax)return null;
          return <text key={ci} x={aX+aW+3} y={toY(y0)+4}
            fontSize="9" fill={cCols[ci]} fontWeight="700">C={C}</text>;
        })}

        {[-2,-1,1,2].map(v=>(
          <g key={v}>
            <text x={toX(v)} y={toY(0)+14} textAnchor="middle" fontSize="9" fill="#888">{v}</text>
            <text x={toX(0)-8} y={toY(v)+4} textAnchor="end" fontSize="9" fill="#888">{v}</text>
          </g>
        ))}
        <text x={aX+aW+8} y={toY(0)+5} fontSize="10" fill="#555">x</text>
        <text x={toX(0)+5} y={aY-7} fontSize="10" fill="#555">y</text>

        {/* RIGHT */}
        <rect x={panX} y={8} width={panW} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={panX+panW/2} y={26} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">
          Exactness Method
        </text>

        {[
          { y:32,  bc:'#2E75B6', bg:'#EBF3FB', title:'Test exactness',
            lines:['M dx+N dy=0','Exact iff: ∂M/∂y=∂N/∂x','∂(2xy+y²)/∂y=2x+2y','∂(x²+2xy)/∂x=2x+2y ✓'] },
          { y:122, bc:'#27ae60', bg:'#f0fff4',  title:'Find potential F',
            lines:['F=∫M dx + g(y)','=x²y+xy²+g(y)','∂F/∂y=x²+2xy+g′(y)=N','g′(y)=0 → g=const'] },
          { y:212, bc:'#c0392b', bg:'#fff0f0',  title:'Write solution',
            lines:['F(x,y) = C  (implicit)','x²y + xy² = C','Apply IC to find C','Verify by taking dF=0'] },
        ].map(({ y, bc, bg, title, lines }) => (
          <g key={y}>
            <rect x={panX+8} y={y} width={panW-16} height={84} rx={4} fill={bg} stroke={bc} strokeWidth="1.3"/>
            <text x={panX+18} y={y+16} fontSize="11" fill={bc} fontWeight="700">{title}</text>
            {lines.map((ln,j)=>(
              <text key={j} x={panX+18} y={y+32+j*16} fontSize="10.5"
                fill={j<2?'#1F3864':'#555'}
                fontFamily={j<2?"'Courier New',monospace":"'Georgia',serif"}
                fontWeight={j<2?'700':'400'}>{ln}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        An ODE M dx + N dy = 0 is exact when ∂M/∂y = ∂N/∂x, meaning M dx + N dy is the total differential dF. Each coloured curve is one level curve of the potential function F(x,y) = x²y + xy² = C. Finding F requires integrating M w.r.t. x to get a candidate, then using ∂F/∂y = N to determine any remaining y-dependent terms.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 5 — IVP: Existence, Uniqueness, Blow-up
// Three side-by-side panels
// ─────────────────────────────────────────────────────────────────
function DiagramIVP() {
  const W = 900, H = 338;
  const panW = (W-28)/3;
  const aH = 172, aYoff = 50;

  const panels = [
    { title:'Unique Solution', sub:'Picard–Lindelöf satisfied',
      color:'#27ae60', fill:'#f0fff4',
      xMin:-1, xMax:2.6, yMin:-1, yMax:5,
      mainFn: x => 2*Math.exp(x)-x-1,
      extras:[ x => 1.4*Math.exp(x)-x-1, x => 2.7*Math.exp(x)-x-1 ],
      ic:{x:0,y:1}, notes:['dy/dx=x+y  Lipschitz','y(0)=1 → unique soln.','C=2 fully determined'] },
    { title:'Non-unique Solutions', sub:'Lipschitz fails at y=0',
      color:'#e07b00', fill:'#fff7ee',
      xMin:-0.1, xMax:3, yMin:-1.2, yMax:3,
      mainFn: _x => 0,
      extras:[ x => Math.pow(Math.max(0,2*x/3),1.5), x => -Math.pow(Math.max(0,2*x/3),1.5) ],
      ic:{x:0,y:0}, notes:['dy/dx=y^(1/3)','∂f/∂y→∞ at y=0','Both curves: y(0)=0'] },
    { title:'Finite-Time Blow-up', sub:'Solution leaves all bounds',
      color:'#c0392b', fill:'#fff0f0',
      xMin:-2, xMax:0.88, yMin:-3, yMax:9,
      mainFn: x => 1/(1-x),
      extras:[ x => 1/(2-x), x => 1/(0.4-x) ],
      ic:{x:0,y:1}, notes:['dy/dx=y²,  y(0)=1','y=1/(1−x)→∞ at x=1','Valid only on x∈(−∞,1)'] },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Initial Value Problems — Existence, Uniqueness (Picard–Lindelöf), and Blow-up</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        {panels.map(({title,sub,color,fill,xMin,xMax,yMin,yMax,mainFn,extras,ic,notes},pi) => {
          const px=10+pi*(panW+4);
          const aX=px+42, aY=aYoff, aW=panW-56;
          const toX=v=>aX+(v-xMin)/(xMax-xMin)*aW;
          const toY=v=>aY+aH-(v-yMin)/(yMax-yMin)*aH;
          const oyC=Math.max(aY,Math.min(aY+aH,toY(0)));
          const oxC=Math.max(aX,Math.min(aX+aW,toX(0)));

          const mkLine=(fn,col,w,dash='')=>{
            const segs=[]; let seg=[];
            for(let i=0;i<=150;i++){
              const x=xMin+(xMax-xMin)*i/150;
              const y=fn(x);
              if(isFinite(y)&&y>=yMin-0.2&&y<=yMax+0.2){
                seg.push(`${toX(x).toFixed(1)},${toY(y).toFixed(1)}`);
              } else { if(seg.length>1)segs.push(seg); seg=[]; }
            }
            if(seg.length>1)segs.push(seg);
            return segs.map((pts,si)=>(
              <polyline key={si} points={pts.join(' ')} fill="none"
                stroke={col} strokeWidth={w} strokeDasharray={dash}
                clipPath={`url(#iv${pi})`}/>
            ));
          };

          return (
            <g key={pi}>
              <rect x={px} y={8} width={panW} height={H-16} rx={5}
                fill={fill} stroke={color} strokeWidth="1.8"/>
              <rect x={px+4} y={12} width={panW-8} height={34} rx={3} fill={color}/>
              <text x={px+panW/2} y={27} textAnchor="middle" fontSize="11" fill="#fff" fontWeight="700">{title}</text>
              <text x={px+panW/2} y={40} textAnchor="middle" fontSize="9.5" fill="#ffffffcc">{sub}</text>

              <clipPath id={`iv${pi}`}><rect x={aX} y={aY} width={aW} height={aH}/></clipPath>
              <rect x={aX} y={aY} width={aW} height={aH} fill="#fff" stroke="#ddd" rx={2}/>
              <line x1={aX} y1={oyC} x2={aX+aW} y2={oyC} stroke="#ccc" strokeWidth="1.2"/>
              <line x1={oxC} y1={aY} x2={oxC}   y2={aY+aH} stroke="#ccc" strokeWidth="1.2"/>

              {extras.map((fn,fi)=>mkLine(fn,color,1.5,'5,3'))}
              {mkLine(mainFn,color,2.8)}

              <circle cx={toX(ic.x)} cy={toY(ic.y)} r={5.5}
                fill={color} stroke="#fff" strokeWidth="1.5"
                clipPath={`url(#iv${pi})`}/>

              {[-2,-1,1,2].filter(v=>v>=xMin&&v<=xMax).map(v=>(
                <text key={v} x={toX(v)} y={oyC+12} textAnchor="middle" fontSize="8" fill="#bbb">{v}</text>
              ))}
              <text x={aX+aW+5} y={oyC+4} fontSize="9" fill="#888">x</text>
              <text x={oxC+3} y={aY-3} fontSize="9" fill="#888">y</text>

              <rect x={px+6} y={aY+aH+12} width={panW-12}
                height={H-16-(aY+aH+12)-8} rx={3} fill="#fff" stroke={color} strokeWidth="0.8"/>
              {notes.map((ln,ni)=>(
                <text key={ni} x={px+panW/2} y={aY+aH+26+ni*17}
                  textAnchor="middle" fontSize="9.5"
                  fill={ni===0?color:'#555'} fontWeight={ni===0?'700':'400'}>{ln}</text>
              ))}
            </g>
          );
        })}
      </svg>
      <div style={S.dCaption}>
        Picard–Lindelöf guarantees a unique local solution when f is continuous and Lipschitz in y near (x₀,y₀). Panel 1: dy/dx = x+y satisfies both — exactly one curve through each point. Panel 2: dy/dx = y^(1/3) fails Lipschitz at y = 0 (∂f/∂y→∞), so both y = 0 and y = (2x/3)^(3/2) satisfy y(0) = 0. Panel 3: dy/dx = y² is smooth everywhere yet the solution y = 1/(1−x) blows up at x = 1 — the interval of existence is bounded even when f is analytic.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 6 — Exponential growth/decay  AND  Logistic growth
// ─────────────────────────────────────────────────────────────────
function DiagramApplications() {
  const W = 900, H = 338;
  const panW = (W-28)/2;
  const aH = 188, aYoff = 52;

  const expCases=[
    {k: 0.7, y0:1, color:'#c0392b', label:'k=0.7 (growth)'},
    {k: 0.3, y0:1, color:'#e07b00', label:'k=0.3 (slow gr.)'},
    {k:-0.5, y0:4, color:'#2E75B6', label:'k=-0.5 (decay)'},
    {k:-1.2, y0:4, color:'#9b59b6', label:'k=-1.2 (fast dec.)'},
  ];
  const K=10, r=0.8;
  const logistic=(t,y0)=>K/(1+(K/y0-1)*Math.exp(-r*t));
  const logCases=[{y0:0.5,color:'#2E75B6'},{y0:2,color:'#27ae60'},{y0:7,color:'#e07b00'},{y0:14,color:'#c0392b'}];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Applications — Exponential Growth/Decay (dy/dt=ky) and Logistic Growth (dy/dt=ry(1−y/K))</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* LEFT: exponential */}
        {(()=>{
          const px=10, aX=px+52, aY=aYoff, aW=panW-66;
          const tMin=0,tMax=4.5,yMin=0,yMax=8;
          const toX=v=>aX+(v-tMin)/(tMax-tMin)*aW;
          const toY=v=>aY+aH-(v-yMin)/(yMax-yMin)*aH;
          return (
            <g>
              <rect x={px} y={8} width={panW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
              <rect x={px+4} y={12} width={panW-8} height={36} rx={3} fill="#1F3864"/>
              <text x={px+panW/2} y={28} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">Exponential: dy/dt = ky</text>
              <text x={px+panW/2} y={42} textAnchor="middle" fontSize="10" fill="#ffffffaa">Solution: y(t) = y₀ · e^(kt)</text>
              <clipPath id="exp1"><rect x={aX} y={aY} width={aW} height={aH}/></clipPath>
              {[1,2,3,4,5,6,7].filter(v=>v<=yMax).map(v=>(
                <line key={v} x1={aX} y1={toY(v)} x2={aX+aW} y2={toY(v)} stroke="#f0f0f0" strokeWidth="0.7"/>
              ))}
              <line x1={aX} y1={aY+aH} x2={aX+aW} y2={aY+aH} stroke="#aaa" strokeWidth="1.4"/>
              <line x1={aX} y1={aY}    x2={aX}    y2={aY+aH} stroke="#aaa" strokeWidth="1.2"/>
              {expCases.map(({k,y0,color},ci)=>{
                const pts=Array.from({length:100},(_,i)=>{
                  const t=tMin+(tMax-tMin)*i/99;
                  const y=y0*Math.exp(k*t);
                  return y>=yMin&&y<=yMax+0.1?`${toX(t).toFixed(1)},${toY(y).toFixed(1)}`:null;
                }).filter(Boolean).join(' ');
                return pts.length>4?<polyline key={ci} points={pts} fill="none"
                  stroke={color} strokeWidth="2.2" clipPath="url(#exp1)"/>:null;
              })}
              {/* Half-life annotation for k=-0.5, y0=4 */}
              {(()=>{
                const k=-0.5, y0=4, th=Math.LN2/0.5, yh=y0/2;
                return (
                  <g clipPath="url(#exp1)">
                    <line x1={toX(th)} y1={toY(yh)} x2={toX(th)} y2={aY+aH}
                      stroke="#2E75B6" strokeWidth="1.1" strokeDasharray="4,3"/>
                    <line x1={aX} y1={toY(yh)} x2={toX(th)} y2={toY(yh)}
                      stroke="#2E75B6" strokeWidth="1.1" strokeDasharray="4,3"/>
                    <text x={toX(th)+3} y={toY(yh)-4} fontSize="9" fill="#2E75B6" fontWeight="700">t½={th.toFixed(2)}</text>
                  </g>
                );
              })()}
              {[0,1,2,3,4].map(v=>(
                <text key={v} x={toX(v)} y={aY+aH+14} textAnchor="middle" fontSize="9" fill="#888">{v}</text>
              ))}
              {[0,2,4,6,8].filter(v=>v<=yMax).map(v=>(
                <text key={v} x={aX-5} y={toY(v)+4} textAnchor="end" fontSize="9" fill="#aaa">{v}</text>
              ))}
              <text x={aX+aW+6} y={aY+aH+4} fontSize="10" fill="#555">t</text>
              <text x={aX+4} y={aY-6} fontSize="10" fill="#555">y</text>
              {expCases.map(({color,label},ci)=>(
                <g key={ci}>
                  <line x1={aX+4} y1={aY+aH+26+ci*14} x2={aX+20} y2={aY+aH+26+ci*14} stroke={color} strokeWidth="2.2"/>
                  <text x={aX+24} y={aY+aH+30+ci*14} fontSize="9" fill={color}>{label}</text>
                </g>
              ))}
            </g>
          );
        })()}

        {/* RIGHT: logistic */}
        {(()=>{
          const px=10+panW+8, aX=px+52, aY=aYoff, aW=panW-66;
          const tMin=0,tMax=11,yMin=0,yMax=16;
          const toX=v=>aX+(v-tMin)/(tMax-tMin)*aW;
          const toY=v=>aY+aH-(v-yMin)/(yMax-yMin)*aH;
          return (
            <g>
              <rect x={px} y={8} width={panW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
              <rect x={px+4} y={12} width={panW-8} height={36} rx={3} fill="#27ae60"/>
              <text x={px+panW/2} y={28} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">Logistic: dy/dt = ry(1 − y/K)</text>
              <text x={px+panW/2} y={42} textAnchor="middle" fontSize="10" fill="#ffffffaa">r=0.8,  K=10  (carrying capacity)</text>
              <clipPath id="log1"><rect x={aX} y={aY} width={aW} height={aH}/></clipPath>
              {[2,4,6,8,10,12,14].filter(v=>v<=yMax).map(v=>(
                <line key={v} x1={aX} y1={toY(v)} x2={aX+aW} y2={toY(v)} stroke="#f0f0f0" strokeWidth="0.7"/>
              ))}
              <line x1={aX} y1={aY+aH} x2={aX+aW} y2={aY+aH} stroke="#aaa" strokeWidth="1.4"/>
              <line x1={aX} y1={aY}    x2={aX}    y2={aY+aH} stroke="#aaa" strokeWidth="1.2"/>
              {/* K line */}
              <line x1={aX} y1={toY(K)} x2={aX+aW} y2={toY(K)}
                stroke="#27ae60" strokeWidth="2" strokeDasharray="7,4" clipPath="url(#log1)"/>
              <text x={aX+aW-3} y={toY(K)-5} textAnchor="end" fontSize="9.5" fill="#27ae60" fontWeight="700">K=10</text>
              {/* K/2 inflection */}
              <line x1={aX} y1={toY(K/2)} x2={aX+aW} y2={toY(K/2)}
                stroke="#888" strokeWidth="1" strokeDasharray="4,4" clipPath="url(#log1)"/>
              <text x={aX+4} y={toY(K/2)-4} fontSize="9" fill="#888">K/2=5 (max growth rate)</text>
              {logCases.map(({y0,color},ci)=>{
                const pts=Array.from({length:100},(_,i)=>{
                  const t=tMin+(tMax-tMin)*i/99;
                  const y=logistic(t,y0);
                  return y>=yMin&&y<=yMax+0.5?`${toX(t).toFixed(1)},${toY(y).toFixed(1)}`:null;
                }).filter(Boolean).join(' ');
                return pts.length>4?<polyline key={ci} points={pts} fill="none"
                  stroke={color} strokeWidth="2.2" clipPath="url(#log1)"/>:null;
              })}
              {[0,2,4,6,8,10].map(v=>(
                <text key={v} x={toX(v)} y={aY+aH+14} textAnchor="middle" fontSize="9" fill="#888">{v}</text>
              ))}
              {[0,4,8,12].filter(v=>v<=yMax).map(v=>(
                <text key={v} x={aX-5} y={toY(v)+4} textAnchor="end" fontSize="9" fill="#aaa">{v}</text>
              ))}
              <text x={aX+aW+6} y={aY+aH+4} fontSize="10" fill="#555">t</text>
              <text x={aX+4} y={aY-6} fontSize="10" fill="#555">y</text>
              {logCases.map(({y0,color},ci)=>(
                <g key={ci}>
                  <line x1={aX+4} y1={aY+aH+26+ci*14} x2={aX+20} y2={aY+aH+26+ci*14} stroke={color} strokeWidth="2.2"/>
                  <text x={aX+24} y={aY+aH+30+ci*14} fontSize="9" fill={color}>y₀={y0}</text>
                </g>
              ))}
            </g>
          );
        })()}
      </svg>
      <div style={S.dCaption}>
        Exponential: dy/dt = ky solves to y = y₀e^(kt). Growth (k &gt; 0) has doubling time ln2/k; decay (k &lt; 0) has half-life ln2/|k| (blue annotation, k=−0.5). Logistic: adding the factor (1−y/K) limits growth to carrying capacity K=10. Maximum growth rate occurs at y=K/2 (grey dashed). All starting values y₀ &gt; 0 converge to K; y₀ &gt; K curves overshoot and descend.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 7 — Euler's Method: numerical vs exact
// ─────────────────────────────────────────────────────────────────
function DiagramEuler() {
  const W = 900, H = 348;
  const exact = x => 2*Math.exp(x)-x-1;
  const fODE  = (x,y) => x+y;
  const x0=0, y0=1, xEnd=2;

  const hCases=[
    {h:0.5,  color:'#c0392b', label:'h=0.5 (coarse)'},
    {h:0.25, color:'#e07b00', label:'h=0.25'},
    {h:0.1,  color:'#27ae60', label:'h=0.1 (fine)'},
  ];

  const aX=56, aY=32, aW=458, aH=244;
  const xMin=0, xMax=2.15, yMin=0, yMax=14;
  const toX=v=>aX+(v-xMin)/(xMax-xMin)*aW;
  const toY=v=>aY+aH-(v-yMin)/(yMax-yMin)*aH;

  const eulerPaths=hCases.map(({h,color,label})=>{
    const pts=[{x:x0,y:y0}];
    let x=x0,y=y0;
    while(x<xEnd-1e-9){
      const nx=Math.min(x+h,xEnd);
      y=y+fODE(x,y)*(nx-x); x=nx;
      pts.push({x,y});
    }
    return {h,color,label,pts};
  });

  const exactPts=Array.from({length:80},(_,i)=>{
    const x=xMin+(xMax-xMin)*i/79;
    const y=exact(x);
    return y>=yMin&&y<=yMax?`${toX(x).toFixed(1)},${toY(y).toFixed(1)}`:null;
  }).filter(Boolean).join(' ');

  const panX=556, panW=W-panX-12;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Euler's Method — Numerical Solution of dy/dx = x+y, y(0)=1  vs  Exact y = 2e^x − x − 1</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        <rect x={10} y={8} width={536} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <rect x={14} y={12} width={528} height={22} rx={3} fill="#1F3864"/>
        <text x={282} y={27} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">
          Euler approximations converge to exact solution (blue) as step h → 0
        </text>

        <clipPath id="eu1"><rect x={aX} y={aY} width={aW} height={aH}/></clipPath>

        {[0,0.5,1,1.5,2].map(v=>(
          <line key={`gx${v}`} x1={toX(v)} y1={aY} x2={toX(v)} y2={aY+aH} stroke="#f0f0f0" strokeWidth="0.7"/>
        ))}
        {[2,4,6,8,10,12].map(v=>(
          <line key={`gy${v}`} x1={aX} y1={toY(v)} x2={aX+aW} y2={toY(v)} stroke="#f0f0f0" strokeWidth="0.7"/>
        ))}
        <line x1={aX} y1={aY+aH} x2={aX+aW} y2={aY+aH} stroke="#aaa" strokeWidth="1.4"/>
        <line x1={aX} y1={aY}    x2={aX}    y2={aY+aH} stroke="#aaa" strokeWidth="1.2"/>

        {/* Exact */}
        <polyline points={exactPts} fill="none" stroke="#2E75B6" strokeWidth="3" clipPath="url(#eu1)"/>

        {/* Euler paths */}
        {eulerPaths.map(({h,color,pts})=>{
          const poly=pts.filter(p=>p.y>=yMin&&p.y<=yMax)
            .map(p=>`${toX(p.x).toFixed(1)},${toY(p.y).toFixed(1)}`).join(' ');
          return (
            <g key={h}>
              <polyline points={poly} fill="none" stroke={color}
                strokeWidth="2" strokeDasharray={h>=0.4?'':'7,4'} clipPath="url(#eu1)"/>
              {h>=0.24&&pts.map((p,i)=>(
                i>0&&p.x<=xMax&&p.y>=yMin&&p.y<=yMax?(
                  <circle key={i} cx={toX(p.x)} cy={toY(p.y)} r={3.5}
                    fill={color} stroke="#fff" strokeWidth="1" clipPath="url(#eu1)"/>
                ):null
              ))}
            </g>
          );
        })}

        {/* IC */}
        <circle cx={toX(0)} cy={toY(1)} r={6} fill="#2E75B6" stroke="#fff" strokeWidth="1.5"/>
        <text x={toX(0)+9} y={toY(1)-7} fontSize="10" fill="#2E75B6" fontWeight="700">(0,1)</text>

        {/* h and hf step labels */}
        {(()=>{
          const p=eulerPaths[0].pts[0], pn=eulerPaths[0].pts[1];
          return (
            <g>
              <line x1={toX(p.x)} y1={toY(p.y)} x2={toX(pn.x)} y2={toY(p.y)}
                stroke="#c0392b" strokeWidth="1.1" strokeDasharray="3,2"/>
              <line x1={toX(pn.x)} y1={toY(p.y)} x2={toX(pn.x)} y2={toY(pn.y)}
                stroke="#c0392b" strokeWidth="1.1" strokeDasharray="3,2"/>
              <text x={(toX(p.x)+toX(pn.x))/2} y={toY(p.y)+13}
                textAnchor="middle" fontSize="8.5" fill="#c0392b">h</text>
              <text x={toX(pn.x)+5} y={(toY(p.y)+toY(pn.y))/2+4}
                fontSize="8.5" fill="#c0392b">hf</text>
            </g>
          );
        })()}

        {/* Axis ticks */}
        {[0,0.5,1,1.5,2].map(v=>(
          <text key={v} x={toX(v)} y={aY+aH+14} textAnchor="middle" fontSize="9" fill="#888">{v}</text>
        ))}
        {[0,2,4,6,8,10,12].map(v=>(
          <text key={v} x={aX-5} y={toY(v)+4} textAnchor="end" fontSize="9" fill="#888">{v}</text>
        ))}
        <text x={aX+aW+8} y={aY+aH+5} fontSize="10" fill="#555">x</text>
        <text x={aX+4} y={aY-7} fontSize="10" fill="#555">y</text>

        {/* Legend */}
        <line x1={aX+4} y1={aY+aH+26} x2={aX+22} y2={aY+aH+26} stroke="#2E75B6" strokeWidth="3"/>
        <text x={aX+26} y={aY+aH+30} fontSize="9.5" fill="#2E75B6" fontWeight="700">Exact solution</text>
        {hCases.map(({color,label},ci)=>(
          <g key={ci}>
            <line x1={aX+146+ci*136} y1={aY+aH+26} x2={aX+164+ci*136} y2={aY+aH+26} stroke={color} strokeWidth="2"/>
            <text x={aX+168+ci*136} y={aY+aH+30} fontSize="9.5" fill={color}>{label}</text>
          </g>
        ))}

        {/* RIGHT: error table + cards */}
        <rect x={panX} y={8} width={panW} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={panX+panW/2} y={26} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">
          Error at x = 2
        </text>

        <rect x={panX+8} y={32} width={panW-16} height={22} rx={3} fill="#1F3864"/>
        <text x={panX+28}  y={46} fontSize="9.5" fill="#fff" fontWeight="700">h</text>
        <text x={panX+78}  y={46} fontSize="9.5" fill="#fff" fontWeight="700">Euler y(2)</text>
        <text x={panX+172} y={46} fontSize="9.5" fill="#fff" fontWeight="700">Error</text>

        {eulerPaths.map(({h,color,pts},i)=>{
          const yA=pts[pts.length-1].y;
          const err=Math.abs(yA-exact(xEnd));
          return (
            <g key={i}>
              <rect x={panX+8} y={58+i*40} width={panW-16} height={36} rx={3}
                fill={i%2===0?'#f8fbff':'#fff'} stroke="#ddd" strokeWidth="0.5"/>
              <text x={panX+28}  y={80+i*40} fontSize="11" fill={color}
                fontFamily="'Courier New',monospace" fontWeight="700">{h}</text>
              <text x={panX+78}  y={80+i*40} fontSize="11" fill="#1F3864"
                fontFamily="'Courier New',monospace">{yA.toFixed(3)}</text>
              <text x={panX+172} y={80+i*40} fontSize="11" fill={color}
                fontFamily="'Courier New',monospace" fontWeight="700">{err.toFixed(3)}</text>
            </g>
          );
        })}

        <rect x={panX+8} y={182} width={panW-16} height={22} rx={3} fill="#EBF3FB" stroke="#2E75B6"/>
        <text x={panX+16} y={196} fontSize="9.5" fill="#2E75B6" fontWeight="700">
          Exact y(2) = {exact(xEnd).toFixed(4)}
        </text>

        {[
          {y:212, bg:'#EBF3FB', bc:'#2E75B6', title:'Euler step formula',
           lines:['yₙ₊₁=yₙ+h·f(xₙ,yₙ)','xₙ₊₁=xₙ+h','Local error ∝ h²']},
          {y:275, bg:'#f0fff4',  bc:'#27ae60', title:'Convergence',
           lines:['Global error ∝ h','Halving h ≈ halves err.','RK4 gives O(h⁴)']},
        ].map(({y,bg,bc,title,lines})=>(
          <g key={y}>
            <rect x={panX+8} y={y} width={panW-16} height={58} rx={4} fill={bg} stroke={bc} strokeWidth="1.2"/>
            <text x={panX+18} y={y+16} fontSize="10.5" fill={bc} fontWeight="700">{title}</text>
            {lines.map((ln,j)=>(
              <text key={j} x={panX+18} y={y+30+j*14} fontSize="10"
                fill={j===0?'#1F3864':'#555'}
                fontFamily={j===0?"'Courier New',monospace":"'Georgia',serif"}
                fontWeight={j===0?'700':'400'}>{ln}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        Euler's method steps along the tangent line: yₙ₊₁ = yₙ + h·f(xₙ,yₙ). Each step commits a local truncation error of O(h²); accumulated over (xEnd−x₀)/h steps the global error is O(h). The table confirms the errors roughly halve as h halves. The step annotation (grey dashes) shows the horizontal advance h and vertical jump h·f for the first coarse step. Runge–Kutta RK4 achieves O(h⁴) global error with four function evaluations per step.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// MAIN
// ─────────────────────────────────────────────────────────────────
export default function FirstOrderODEs() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>First-Order Ordinary Differential Equations</h1>

      <p style={S.p}>
        A first-order ordinary differential equation (ODE) involves an unknown function y(x) and its first derivative dy/dx only. The general form is dy/dx = f(x, y). Geometrically, f assigns a slope to every point in the xy-plane, filling it with a slope field whose integral curves are the solutions. The challenge is either to find y(x) in closed form or — when that is impossible — to understand the solution's behaviour qualitatively from the slope field or approximate it numerically. First-order ODEs arise throughout science: radioactive decay, population dynamics, mixing problems, electrical circuits, and Newton's law of cooling all reduce to one of the four canonical types covered on this page.
      </p>

      <InfoBox
        title="Classification of First-Order ODEs"
        items={[
          "General form: dy/dx = f(x, y) — equivalently M(x,y)dx + N(x,y)dy = 0.",
          "Separable: f(x,y) = h(x)·g(y) — divide by g(y) and integrate each side independently.",
          "Linear: dy/dx + P(x)y = Q(x) — multiply by integrating factor μ(x) = e^(∫P dx).",
          "Exact: ∂M/∂y = ∂N/∂x — M dx + N dy is a total differential dF; implicit solution F = C.",
          "Bernoulli: dy/dx + P(x)y = Q(x)yⁿ — substitution v = y^(1−n) linearises the equation.",
          "Homogeneous: f(x,y) = g(y/x) — substitution v = y/x reduces to separable form.",
          "Initial Value Problem (IVP): dy/dx = f(x,y) with y(x₀) = y₀ — one initial condition selects a unique solution from the family.",
          "Existence & Uniqueness (Picard–Lindelöf): if f and ∂f/∂y are continuous near (x₀,y₀), a unique solution exists locally.",
        ]}
      />

      <h2 style={S.h2}>Slope Fields and Direction Fields</h2>
      <DiagramSlopeField />
      <Table2 rows={[
        ["Slope field",         "A short tick at each (x,y) with angle = f(x,y)\nTangent to the solution curve passing through that point"],
        ["Isoclines",           "Curves where dy/dx = k is constant; y−x=k gives y=x+k here\nDraw several isoclines first, then sketch solution curves between them"],
        ["Zero isocline",       "Where dy/dx=0; solutions cross horizontally here\nFor dy/dx=y−x: zero isocline is the line y=x"],
        ["Equilibrium solution","Constant y=c where f(x,c)=0 for all x\nAppears as a horizontal line; may be stable or unstable"],
        ["Autonomous ODE",      "dy/dx=f(y) only — field identical in every column\nPhase-line analysis: sign of f determines increasing/decreasing"],
        ["General solution",    "One-parameter family y=φ(x,C)\nIC y(x₀)=y₀ uniquely determines C (when uniqueness holds)"],
      ]} />

      <h2 style={S.h2}>Separable ODEs</h2>
      <DiagramSeparable />
      <Table2 rows={[
        ["Separable form",      "dy/dx = h(x)·g(y)  →  dy/g(y) = h(x) dx\nRequires g(y)≠0; check g(y)=0 separately for equilibria"],
        ["Integration step",   "∫dy/g(y) = ∫h(x)dx + C\nOne constant suffices; it absorbs both anti-derivative constants"],
        ["Equilibrium solutions","g(y₀)=0 gives constant solution y=y₀\nLost when dividing by g(y); must be verified in original ODE"],
        ["Example: dy/dx=xy",   "∫dy/y = ∫x dx → ln|y|=x²/2+C → y=Ae^(x²/2)\nEquilibrium y=0 confirmed; not obtainable from general family"],
        ["Implicit solutions",  "If ∫dy/g(y) has no closed form, leave as G(y)=H(x)+C\nResolve numerically if explicit y(x) is required"],
        ["Interval of validity","Solution valid on largest interval where no singularity occurs\nExample: dy/dx=y², y(0)=1 → y=1/(1−x), valid for x<1"],
      ]} />

      <h2 style={S.h2}>Linear First-Order ODEs — Integrating Factor</h2>
      <DiagramLinear />
      <Table2 rows={[
        ["Standard form",       "dy/dx + P(x)y = Q(x) — P and Q functions of x only\nAlways rearrange to this form before applying the method"],
        ["Integrating factor",  "μ(x) = e^(∫P(x)dx) — no constant of integration needed\nKey identity: μ′(x) = μ(x)·P(x)"],
        ["Product rule reversal","Multiply ODE by μ: left side = d/dx[μ(x)y]\nExact because μ′y + μy′ = (μy)′"],
        ["General solution",    "μ(x)·y = ∫μ(x)Q(x)dx + C  →  y=(1/μ)(∫μQ dx+C)\n+C gives full one-parameter family"],
        ["Applying IC",         "Substitute y(x₀)=y₀ into general solution; solve for C\nC uniquely determined when integral evaluated at x₀"],
        ["Example",             "dy/dx+(2/x)y=x², μ=x², d/dx[x²y]=x⁴\nx²y=x⁵/5+C → y=x³/5+C/x²; y(1)=1 gives C=4/5"],
      ]} />

      <h2 style={S.h2}>Exact ODEs</h2>
      <DiagramExact />
      <Table2 rows={[
        ["Exact condition",      "M dx+N dy=0 exact iff ∂M/∂y = ∂N/∂x\nEquivalent to: M dx+N dy is the total differential dF(x,y)"],
        ["Finding potential F",  "Integrate ∂F/∂x=M w.r.t. x: F=∫M dx+g(y)\nDifferentiate w.r.t. y, set equal to N, solve for g′(y)"],
        ["Implicit solution",    "F(x,y) = C  (implicit equation)\nDifferentiate implicitly to recover the original ODE as a check"],
        ["Integrating factor",   "If not exact, find μ so μM dx+μN dy=0 is exact\n(∂M/∂y−∂N/∂x)/N = function of x alone → μ=e^∫that dx"],
        ["Example check",        "M=2xy+y², N=x²+2xy\n∂M/∂y=2x+2y=∂N/∂x ✓ → proceed to find F"],
        ["Example solution",     "F=∫(2xy+y²)dx=x²y+xy²+g(y)\n∂F/∂y=x²+2xy+g′=N → g′=0 → F=x²y+xy²=C"],
      ]} />

      <h2 style={S.h2}>Initial Value Problems — Existence and Uniqueness</h2>
      <DiagramIVP />
      <Table2 rows={[
        ["Picard–Lindelöf",     "If f continuous and Lipschitz in y on a rectangle R about (x₀,y₀),\nthe IVP dy/dx=f, y(x₀)=y₀ has a unique solution on some interval"],
        ["Lipschitz condition",  "|f(x,y₁)−f(x,y₂)| ≤ L|y₁−y₂| for all y₁,y₂ in R\nEquivalent to |∂f/∂y|≤L when f is differentiable"],
        ["Non-uniqueness",       "dy/dx=y^(1/3) at y=0: ∂f/∂y=(1/3)y^(−2/3)→∞, Lipschitz fails\ny=0 and y=(2x/3)^(3/2) both satisfy y(0)=0"],
        ["Finite-time blow-up",  "dy/dx=y²: solution y=y₀/(1−y₀x) → ∞ as x→1/y₀\nf is smooth but the interval of existence [0,1/y₀) is bounded"],
        ["Peano existence thm.",  "Continuity of f (without Lipschitz) guarantees existence but not uniqueness\nWeaker result; needed when Lipschitz cannot be verified"],
        ["Interval of existence","Largest interval around x₀ on which the unique solution persists\nCan be limited by blow-up, singularity of P or Q, or domain boundary"],
      ]} />

      <h2 style={S.h2}>Applications — Exponential Growth/Decay and Logistic Growth</h2>
      <DiagramApplications />
      <Table2 rows={[
        ["Exponential growth",   "dy/dt=ky, k>0 → y=y₀e^(kt)\nDoubling time T₂=ln2/k; uninhibited population, compound interest"],
        ["Exponential decay",    "dy/dt=ky, k<0 → y=y₀e^(kt)\nHalf-life t½=ln2/|k|; radioactive decay, drug clearance"],
        ["Newton's cooling",     "dT/dt=−k(T−Tₐ) → T=Tₐ+(T₀−Tₐ)e^(−kt)\nLinear ODE; temperature approaches ambient Tₐ exponentially"],
        ["Logistic growth",      "dy/dt=ry(1−y/K) → y=K/(1+(K/y₀−1)e^(−rt))\nStable equilibrium K (carrying capacity); unstable equilibrium 0"],
        ["Logistic phase line",  "dy/dt>0 for 0<y<K; dy/dt<0 for y>K\nInflection (max growth rate) at y=K/2; sigmoid-shaped curve"],
        ["Mixing problems",      "dy/dt = rate_in − rate_out → linear first-order ODE\nSolve with integrating factor; common in chemical/environmental eng."],
      ]} />

      <h2 style={S.h2}>Euler's Method — Numerical Approximation</h2>
      <DiagramEuler />
      <Table2 rows={[
        ["Euler's method",       "yₙ₊₁=yₙ+h·f(xₙ,yₙ),  xₙ₊₁=xₙ+h\nSteps along tangent line with slope f(xₙ,yₙ)"],
        ["Local truncation error","Error per step = (h²/2)y″(xₙ)+O(h³) = O(h²)\nFrom Taylor expansion truncated after the linear term"],
        ["Global error",         "Accumulated over N=(xEnd−x₀)/h steps: O(h)\nHalving h approximately halves the global error"],
        ["Stability condition",  "Explicit Euler stable if h·|∂f/∂y| < 2\nStiff ODEs (large |∂f/∂y|) require implicit methods"],
        ["Runge–Kutta RK4",     "Four slope evaluations per step; global error O(h⁴)\nyₙ₊₁=yₙ+(h/6)(k₁+2k₂+2k₃+k₄); standard go-to method"],
        ["Adaptive step (ode45)","Compare RK4 and RK5 estimates to control local error\nAutomatically reduces h where solution changes rapidly"],
      ]} />

      <InfoBox
        title="Key Takeaways — First-Order ODEs"
        items={[
          "Identify the type first: separable, linear, exact, Bernoulli, or homogeneous — each has a specific algorithm.",
          "Slope fields give qualitative behaviour without solving: sketch isoclines first, then trace integral curves.",
          "Separable: divide by g(y), integrate each side, check g(y)=0 separately for equilibrium solutions.",
          "Linear dy/dx + P(x)y = Q(x): compute μ = e^(∫P dx), multiply through, integrate d/dx[μy] = μQ.",
          "Exact M dx + N dy = 0: verify ∂M/∂y = ∂N/∂x, then find F by integrating M w.r.t. x and matching ∂F/∂y = N.",
          "Picard–Lindelöf requires both continuity and Lipschitz in y for uniqueness — verify before assuming one solution.",
          "Equilibrium solutions (where f=0 or g(y)=0) must always be checked separately from the general family.",
          "Euler's method has O(h) global error; Runge–Kutta RK4 achieves O(h⁴). Use adaptive solvers (ode45) for practical computation.",
        ]}
      />
    </div>
  );
}